import {HttpClient} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {IBusinessPartner, IRawData, IVehicle, IVehicleTypeInformation, Vehicle} from '@service-and-repairs/awpintegrationlib';
import {CosyService} from '../../cosy/cosy.service';
import {RotationImage} from '../../cosy/rotation-image';
import {UserData} from '../../user/models/user-data';

interface RotationImageResponse {
  readonly rotationImages?: readonly RotationImage[];
}

interface VehiclesResponse {
  readonly data: {
    readonly vehicles: readonly (IRawData & RotationImageResponse)[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly rotationImages = signal<ReadonlyMap<string, readonly RotationImage[]>>(new Map());
  private readonly noRotationImages: readonly RotationImage[] = [];

  getRotationImages(imageUrl: string): readonly RotationImage[] {
    return this.rotationImages().get(imageUrl) ?? this.noRotationImages;
  }

  getVehicles(vin: string, user: UserData): Promise<IVehicle[]> {
    return new Promise((resolve, reject): void => {
      const userParameters = `?locale=${user.getLocale()}&country=${user.getB2XCountryCode()}`;
      const businessPartner: IBusinessPartner = user.getBusinessPartner();
      const dealerParameters: string = businessPartner
        ? `&distribution_partner_number=${businessPartner.getDistributionPartnerNumber()}&outlet_number=${businessPartner.getOutletNumber()}`
        : '';
      this.httpClient.get<VehiclesResponse>('/api/vehicle/v2/' + vin + userParameters + dealerParameters, {withCredentials: true})
        .subscribe({
          next: (response: VehiclesResponse): void => {
            const vehicles: IVehicle[] = response.data.vehicles.map(
              ({rotationImages, ...rawVehicle}: IRawData & RotationImageResponse): IVehicle => {
                const vehicle: IVehicle = CosyService.addBasePathToCosyUrls(Vehicle.fromRawData(rawVehicle));
                this.rememberRotationImages(vehicle, rotationImages);
                return vehicle;
              }
            );
            resolve(vehicles);
          },
          error: (reason: unknown): void => reject(reason)
        });
    });
  }

  getVirtualVehicle(typeCode: string, user: UserData): Promise<IVehicle> {
    return new Promise((resolve, reject): void => {
      const parameters: string = `?locale=${user.getLocale()}&country=${user.getB2XCountryCode()}`
        + `&distribution_partner_number=${user.getBusinessPartner()?.getDistributionPartnerNumber()}`
        + `&outlet_number=${user.getBusinessPartner()?.getOutletNumber()}`;
      this.httpClient.get<IVehicleTypeInformation & RotationImageResponse>(
        '/api/typecode/v1/' + typeCode + parameters, {withCredentials: true}
      )
        .subscribe({
          next: ({rotationImages, ...typeInformation}: IVehicleTypeInformation & RotationImageResponse): void => {
            const vehicle: IVehicle = CosyService.addBasePathToCosyUrls(Vehicle.createVirtualVehicle(typeInformation));
            this.rememberRotationImages(vehicle, rotationImages);
            resolve(vehicle);
          },
          error: (reason: unknown): void => reject(reason)
        });
    });
  }

  private rememberRotationImages(vehicle: IVehicle, images: readonly RotationImage[] = []): void {
    const resolvedImages: readonly RotationImage[] = CosyService.addBasePathToRotationImages(images);
    this.rotationImages.update((imagesByPreview: ReadonlyMap<string, readonly RotationImage[]>): ReadonlyMap<string, readonly RotationImage[]> =>
      new Map(imagesByPreview).set(vehicle.getImageUrlSmall(), resolvedImages)
    );
  }
}
